import React, { useState } from 'react'
import { collection } from 'firebase/firestore'
import { db } from '@/helpers/firebase'
import { useTonAddress } from '@tonconnect/ui-react'
import { callCreateOrder } from '@/helpers/api'
import { CardPack } from 'common/schema/cardPacks'
import { useCachedPublicImage } from '@/helpers/urls'
import { useFirestoreCollectionCache } from '@/hooks/useFirestoreCache'
import '@/screens/Store.css'

// Helper component for Pack Cards with cached images
const PackCard: React.FC<{
  pack: CardPack
  isProcessing: boolean
  onPurchase: () => void
  isWalletConnected: boolean
}> = ({ pack, isProcessing, onPurchase, isWalletConnected }) => {
  const cachedImageUrl = useCachedPublicImage(pack.url)

  return (
    <div className="store__card-wrapper">
      <div className="store__card">
        <div
          className="store__card-image"
          style={{
            backgroundImage: cachedImageUrl ? `url(${cachedImageUrl})` : 'none',
          }}
        >
          <div className="store__card-overlay">
            <div className="store__card-content">
              <h2 className="store__card-title">{pack.collection}</h2>
              <p className="store__card-category">{pack.category}</p>
              <div className="store__card-price">
                <span className="store__price-amount">${pack.price}</span>
                <span className="store__price-currency">USD</span>
              </div>
              <button
                className={`store__purchase-btn ${
                  isProcessing ? 'processing' : ''
                }`}
                onClick={onPurchase}
                disabled={!isWalletConnected || isProcessing}
              >
                {!isWalletConnected ? (
                  'Connect Wallet to Purchase'
                ) : isProcessing ? (
                  <>
                    <div className="store__btn-spinner" />
                    Processing...
                  </>
                ) : (
                  'Purchase Pack'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const Store: React.FC = () => {
  const [processingPurchase, setProcessingPurchase] = useState<string | null>(
    null
  )
  const walletAddress = useTonAddress()

  // Use cached Firestore queries - now without wallet dependency
  const {
    data: cardPacks = [],
    loading,
    error,
  } = useFirestoreCollectionCache<CardPack>(
    collection(db, 'cardPacks'),
    'store-cardPacks',
    (snapshot) => {
      const packs: CardPack[] = []
      snapshot.forEach((doc) => {
        packs.push({ cardPackId: doc.id, ...doc.data() } as CardPack)
      })
      return packs
    },
    true,
    false // requireAuth: false
  )

  const handlePurchase = async (cardPack: CardPack) => {
    if (!walletAddress) {
      alert('Please connect your wallet first')
      return
    }

    setProcessingPurchase(cardPack.cardPackId)
    try {
      const result = await callCreateOrder({ cardPackId: cardPack.cardPackId })
      if (result.success) {
        window.open(result.response.data.webUrl, '_blank')
      } else {
        alert(`Purchase failed: ${result.error?.message}`)
      }
    } catch (error) {
      console.error('Purchase error:', error)
      alert('Failed to process purchase')
    } finally {
      setProcessingPurchase(null)
    }
  }

  if (loading) {
    return (
      <div className="store__loader">
        <div className="store__spinner"></div>
        <p>Loading card packs...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="store__error">
        <p>Failed to load card packs. Please try again later.</p>
      </div>
    )
  }

  return (
    <div className="store">
      <div className="store__grid">
        {cardPacks.map((pack) => (
          <PackCard
            key={pack.cardPackId}
            pack={pack}
            isProcessing={processingPurchase === pack.cardPackId}
            onPurchase={() => handlePurchase(pack)}
            isWalletConnected={!!walletAddress}
          />
        ))}
      </div>
    </div>
  )
}
